import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { Map, BarChart3, Settings, LogOut, Users, Flag, Building2 } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export default function AdminLayout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  const navItems: {
    to: string
    label: string
    icon: typeof Map
    roles?: string[]
  }[] = [
    { to: '/admin/hunts', label: 'My Hunts', icon: Map },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/admin/sponsor', label: 'Sponsor', icon: Building2, roles: ['business', 'admin'] },
    { to: '/admin/users', label: 'Users', icon: Users, roles: ['admin'] },
    { to: '/admin/reports', label: 'Reports', icon: Flag, roles: ['admin'] },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ]

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col bg-gradient-to-b from-[#78350f] to-[#92400e] text-white">
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#f59e0b]">
            <Map className="h-6 w-6 text-[#78350f]" />
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight">Loot Seeker</h1>
            <p className="text-xs text-amber-200">Creator Studio</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 space-y-1 px-3">
          {navItems.filter((item) => !item.roles || (user?.role && item.roles.includes(user.role))).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#f59e0b] text-[#78350f]'
                    : 'text-amber-100 hover:bg-white/10'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-600 text-sm font-bold">
              {user?.display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 truncate">
              <p className="truncate text-sm font-medium">
                {user?.display_name || 'User'}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-amber-200 transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-stone-50">
        <Outlet />
      </main>
    </div>
  )
}
